
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { User, MapPin, Satellite, Map } from "lucide-react";
import { Profile } from "@/hooks/useProfile";
import { HardinessZoneSelector } from "@/components/HardinessZoneSelector";
import { LocationService, countries, usCities } from "@/services/locationService";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const farmSizes = [
  "Less than 1 acre",
  "1-5 acres",
  "6-20 acres",
  "21-50 acres",
  "51-100 acres",
  "101-500 acres",
  "501-1,000 acres",
  "More than 1,000 acres"
];

interface ProfileFormProps {
  profile: Profile;
  avatarUploading: boolean;
  onAvatarUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onUpdateProfile: (field: keyof Profile, value: string) => void;
  onUpdateHardinessZone: (zoneId: string) => void;
}

export const ProfileForm = ({ profile, avatarUploading, onAvatarUpload, onUpdateProfile, onUpdateHardinessZone }: ProfileFormProps) => {
  const [locationMethod, setLocationMethod] = useState<'gps' | 'manual'>('manual');
  const [gpsLoading, setGpsLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedZoneInfo, setSelectedZoneInfo] = useState<{ zone_code: string; zone_name: string; zone_system: string } | null>(null);

  // Fetch zone information when hardiness_zone_id changes
  useEffect(() => {
    const fetchZoneInfo = async () => {
      if (profile.hardiness_zone_id && locationMethod === 'gps') {
        try {
          const { data } = await supabase
            .from('hardiness_zones')
            .select('zone_code, zone_name, zone_system')
            .eq('id', profile.hardiness_zone_id)
            .single();
          
          if (data) {
            setSelectedZoneInfo(data);
          }
        } catch (error) {
          console.error('Error fetching zone info:', error);
        }
      } else {
        setSelectedZoneInfo(null);
      }
    };
    
    fetchZoneInfo();
  }, [profile.hardiness_zone_id, locationMethod]);

  const handleGPSLocation = async () => {
    console.log('🗺️ Starting GPS location request');
    setGpsLoading(true);
    
    try {
      console.log('🔍 Calling LocationService.getGPSLocation()');
      const locationInfo = await LocationService.getGPSLocation();
      console.log('✅ GPS location received:', locationInfo);
      
      // Update profile with GPS location
      console.log('📍 Updating profile location:', locationInfo.formattedAddress);
      onUpdateProfile('location', locationInfo.formattedAddress);
      setSelectedCountry(locationInfo.country);
      setSelectedCity(locationInfo.city);
      
      // Automatically set the appropriate zone based on GPS location
      if (locationInfo.zoneId) {
        console.log('🌡️ Zone detected:', locationInfo.zoneId, 'Type:', locationInfo.zoneType);
        if (locationInfo.zoneType === 'hardiness') {
          console.log('🔧 Calling onUpdateHardinessZone with:', locationInfo.zoneId);
          onUpdateHardinessZone(locationInfo.zoneId);
          console.log('✅ Hardiness zone updated successfully');
          toast.success(`Growing zone detected for ${locationInfo.country}!`);
        } else if (locationInfo.zoneType === 'climate') {
          console.log('🌍 Climate zone detected:', locationInfo.zoneId);
          toast.success(`Climate zone ${locationInfo.zoneId} detected for ${locationInfo.country}`);
        }
      } else {
        console.log('⚠️ No zone detected for location');
        toast.info('Location detected but growing zone needs to be set manually');
      }
      
      toast.success(`Location updated: ${locationInfo.city}, ${locationInfo.country}`);
      
      console.log('🎉 GPS location process completed successfully');
    } catch (error) {
      console.error('❌ GPS location error:', error);
      console.error('Error details:', error);
      toast.error('Failed to get GPS location. Please try manual selection.');
    } finally {
      setGpsLoading(false);
      console.log('🏁 GPS loading state reset');
    }
  };

  const handleManualLocationChange = () => {
    if (selectedCountry && selectedCity) {
      const location = `${selectedCity}, ${selectedCountry}`;
      onUpdateProfile('location', location);
    }
  };

  // Update location when country or city changes
  const updateCountry = (country: string) => {
    setSelectedCountry(country);
    setSelectedCity(''); // Reset city when country changes
    if (country && selectedCity) {
      onUpdateProfile('location', `${selectedCity}, ${country}`);
    }
  };

  const updateCity = (city: string) => {
    setSelectedCity(city);
    if (selectedCountry && city) {
      onUpdateProfile('location', `${city}, ${selectedCountry}`);
    }
  };
  return (
    <>
      {/* Basic Profile Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center text-green-700">
            <User className="mr-2" size={20} />
            Profile Information
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <Label htmlFor="username" className="text-sm">Username</Label>
                <Input
                  id="username"
                  value={profile.username || ""}
                  onChange={(e) => onUpdateProfile("username", e.target.value)}
                  placeholder="Enter your username"
                  className="h-9"
                />
              </div>
              
              <div>
                <Label htmlFor="farm_name" className="text-sm">Farm Name</Label>
                <Input
                  id="farm_name"
                  value={profile.farm_name || ""}
                  onChange={(e) => onUpdateProfile("farm_name", e.target.value)}
                  placeholder="Enter your farm name"
                  className="h-9"
                />
              </div>
              
              <div>
                <Label htmlFor="farm_website" className="text-sm">Farm Website</Label>
                <Input
                  id="farm_website"
                  value={profile.farm_website || ""}
                  onChange={(e) => onUpdateProfile("farm_website", e.target.value)}
                  placeholder="https://yourfarm.com"
                  className="h-9"
                />
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <Label htmlFor="farm_size" className="text-sm">Farm Size</Label>
                <Select
                  value={profile.farm_size || ""}
                  onValueChange={(value) => onUpdateProfile("farm_size", value)}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select your farm size" />
                  </SelectTrigger>
                  <SelectContent>
                    {farmSizes.map((size) => (
                      <SelectItem key={size} value={size}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="farming_since" className="text-sm">Farming Since</Label>
                <Input
                  id="farming_since"
                  type="date"
                  value={profile.farming_since || ""}
                  onChange={(e) => onUpdateProfile("farming_since", e.target.value)}
                  className="h-9"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Location & Growing Zone - Compact */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center text-green-700 text-sm">
            <MapPin className="mr-2" size={16} />
            Location & Growing Zone
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 pt-0">
          <div className="space-y-3">
            {/* Location Method Selection */}
            <div className="space-y-1">
              <Label className="text-xs font-medium">Location Method</Label>
              <RadioGroup
                value={locationMethod}
                onValueChange={(value) => setLocationMethod(value as 'gps' | 'manual')}
                className="flex flex-row space-x-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="gps" id="gps" />
                  <Label htmlFor="gps" className="text-xs cursor-pointer flex items-center">
                    <Satellite className="mr-1" size={10} />
                    Use GPS
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="manual" id="manual" />
                  <Label htmlFor="manual" className="text-xs cursor-pointer flex items-center">
                    <Map className="mr-1" size={10} />
                    Select Manually
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* GPS Location */}
            {locationMethod === 'gps' && (
              <div className="space-y-2">
                <Button 
                  onClick={handleGPSLocation}
                  disabled={gpsLoading}
                  className="w-full h-7 text-xs"
                  variant="outline"
                >
                  {gpsLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary mr-2"></div>
                      Getting Location...
                    </>
                  ) : (
                    <>
                      <Satellite className="mr-2" size={10} />
                      Get My Location
                    </>
                  )}
                </Button>
                {profile.location && (
                  <div className="p-2 bg-green-50 rounded-md">
                    <p className="text-xs text-green-700">
                      <MapPin className="inline mr-1" size={10} />
                      Current: {profile.location}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Manual Location Selection */}
            {locationMethod === 'manual' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="country" className="text-xs">Country</Label>
                  <Select
                    value={selectedCountry}
                    onValueChange={updateCountry}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Select your country" />
                    </SelectTrigger>
                    <SelectContent className="max-h-48 z-50">
                      {countries.map((country) => (
                        <SelectItem key={country} value={country}>
                          {country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="city" className="text-xs">City</Label>
                  {selectedCountry === 'United States' ? (
                    <Select
                      value={selectedCity}
                      onValueChange={updateCity}
                      disabled={!selectedCountry}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue placeholder="Select your city" />
                      </SelectTrigger>
                      <SelectContent className="max-h-48 z-50">
                        {usCities.map((city) => (
                          <SelectItem key={city} value={city}>
                            {city}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id="city"
                      value={selectedCity}
                      onChange={(e) => updateCity(e.target.value)}
                      placeholder={selectedCountry ? "Enter your city" : "Select country first"}
                      disabled={!selectedCountry}
                      className="h-8 text-sm"
                    />
                  )}
                </div>

                {profile.location && (
                  <div className="md:col-span-2">
                    <div className="p-2 bg-blue-50 rounded-md">
                      <p className="text-xs text-blue-700">
                        <MapPin className="inline mr-1" size={10} />
                        Location: {profile.location}
                      </p>
                    </div>
                  </div>
                )}

                {/* Hardiness zone selector - inline layout */}
                <div className="md:col-span-2">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs font-medium min-w-[90px] shrink-0">Hardiness Zone</Label>
                    <div className="flex-1 min-w-0">
                      <HardinessZoneSelector
                        value={profile.hardiness_zone_id || ""}
                        onValueChange={onUpdateHardinessZone}
                        placeholder="Select zone"
                        showLabel={false}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

              {/* Show zone info for GPS location */}
              {locationMethod === 'gps' && profile.hardiness_zone_id && selectedZoneInfo && (
                <div className="p-2 bg-green-50 border border-green-200 rounded-md">
                  <div className="flex items-start space-x-2">
                    <Satellite className="text-green-600 mt-0.5" size={12} />
                    <div>
                      <p className="text-xs font-medium text-green-800">
                        Growing Zone Detected
                      </p>
                      <p className="text-xs text-green-700">
                        {selectedZoneInfo.zone_system} {selectedZoneInfo.zone_code} - {selectedZoneInfo.zone_name}
                      </p>
                      <p className="text-xs text-green-600 mt-1">
                        Automatically set based on your GPS location
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Fallback message if zone info not available */}
              {locationMethod === 'gps' && profile.hardiness_zone_id && !selectedZoneInfo && (
                <div className="p-2 bg-green-50 rounded-md">
                  <p className="text-xs text-green-700">
                    <Satellite className="inline mr-1" size={10} />
                    Hardiness zone automatically detected from GPS location
                  </p>
                </div>
              )}
          </div>
        </CardContent>
      </Card>

      {/* Bio Section - Compact */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center text-green-700 text-sm">
            <User className="mr-2" size={16} />
            About Your Farm
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 pt-0">
          <div>
            <Label htmlFor="bio" className="text-xs">Farm Bio</Label>
            <Textarea
              id="bio"
              value={profile.bio || ""}
              onChange={(e) => onUpdateProfile("bio", e.target.value)}
              placeholder="Tell us about yourself, your farming journey, practices, and what makes your farm unique..."
              rows={3}
              className="resize-none text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              Share your farming story, practices, and what makes your farm special.
            </p>
          </div>
        </CardContent>
      </Card>
    </>
  );
};
